import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Card,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip as MuiTooltip,
} from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import SoftBadge from 'components/SoftBadge';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import {
  getAllTenants,
  getTenant,
  getTenantBilling,
  updateTenant,
  extendTrial,
  getTenantUsers,
  createTenantUser,
  disableTenantUser,
  enableTenantUser,
  updateTenantUserRole,
  deleteTenantUser,
  deactivateTenant,
  activateTenant,
  updateTenantFeatures,
  archiveTenant,
  restoreTenant,
  getArchiveStatus,
} from 'services/tenantService';
import { getTenantStats } from 'services/statsService';

const STAT_LABEL_MAP = {
  'id-scans': 'ID Scans',
  'credit-pull': 'Credit Pulls',
  'dmv': 'DMV',
  'id_visual_analysis': 'ID Visual Analysis',
  'business_verification': 'Business Verification',
  'email_verification': 'Email Verification',
  'phone_verification': 'Phone Verification',
  'ip_verification': 'IP Verification',
  'fraud_list_check': 'Fraud List Check',
  'credit_fraud_alerts': 'Credit Fraud Alerts',
};

const STAT_COLORS = [
  '#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2',
  '#0097a7', '#c2185b', '#455a64', '#fbc02d', '#5d4037',
];

const tenantValidationSchema = Yup.object({
  businessName: Yup.string().required('Business name is required'),
  tenantName: Yup.string().required('Short name is required'),
  tenantEmail: Yup.string().email('Must be a valid email').required('Email is required'),
  tenantPhone: Yup.string().required('Phone is required'),
  tenantAddress: Yup.string(),
});

const ROLE_COLORS = {
  TenantAdmin: 'primary',
  Admin: 'info',
  Dashboard: 'secondary',
  TenantUser: 'default',
};

function TenantDashboard() {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [plan, setPlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [extendingTrial, setExtendingTrial] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState('');
  const [trialScans, setTrialScans] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Tenant Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [addUserSubmitting, setAddUserSubmitting] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('TenantUser');
  const [userActionDialog, setUserActionDialog] = useState({ open: false, user: null, action: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [editRoleDialog, setEditRoleDialog] = useState({ open: false, user: null, role: '' });
  const [tenantStatusDialog, setTenantStatusDialog] = useState(false);
  const [tenantStats, setTenantStats] = useState(null);
  const [tenantStatsMonths, setTenantStatsMonths] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [softPullEnabled, setSoftPullEnabled] = useState(false);
  const [softPullUpdating, setSoftPullUpdating] = useState(false);
  const [archiveInfo, setArchiveInfo] = useState({
    archiveStatus: 'NONE', archived: false, archiveKey: null,
    archivedAt: null, archiveError: null, archiveCounts: null,
  });
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveConfirmText, setArchiveConfirmText] = useState('');
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      businessName: '',
      tenantName: '',
      tenantEmail: '',
      tenantPhone: '',
      tenantAddress: '',
    },
    validationSchema: tenantValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        await updateTenant(tenantId, values);
        setSnackbar({ open: true, message: 'Tenant updated successfully!', severity: 'success' });
      } catch (err) {
        console.error('Error updating tenant:', err);
        setSnackbar({ open: true, message: 'Failed to update tenant', severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const data = await getTenantUsers(tenantId);
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading tenant users:', err);
      setSnackbar({ open: true, message: 'Failed to load tenant users', severity: 'error' });
    } finally {
      setUsersLoading(false);
    }
  };

  const loadArchiveStatus = async () => {
    try {
      const data = await getArchiveStatus(tenantId);
      const info = data?.message || data;
      if (info && info.archiveStatus) setArchiveInfo(info);
      return info;
    } catch (err) {
      console.error('Error loading archive status:', err);
      return null;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [tenantData, billingData, allTenants] = await Promise.all([
          getTenant(tenantId),
          getTenantBilling(tenantId),
          getAllTenants(),
        ]);
        const t = tenantData?.message || tenantData;
        const tenantRecord = (Array.isArray(allTenants) ? allTenants : [])
          .find((rec) => rec.tenantId === tenantId);
        t.is_active = tenantRecord?.isActive !== false;
        setTenant(t);
        formik.setValues({
          businessName: t?.business_name || '',
          tenantName: t?.tenant_name || '',
          tenantEmail: t?.tenant_email || '',
          tenantPhone: t?.tenant_phone || '',
          tenantAddress: t?.tenant_address || '',
        });

        const billing = Array.isArray(billingData) ? billingData : [];
        const planItem = billing.find(b => b.sk === 'plan') || null;
        setPlan(planItem);
        setSoftPullEnabled(planItem?.data?.softPullEnabled === true);
        if (planItem?.data?.isTrial && planItem?.data?.currentPeriodEnd) {
          setTrialEndDate(new Date(planItem.data.currentPeriodEnd * 1000).toISOString().split('T')[0]);
          setTrialScans(String(planItem.data.scans || 0));
        }
      } catch (err) {
        console.error('Error loading tenant dashboard:', err);
        setSnackbar({ open: true, message: 'Failed to load tenant data', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    loadData();
    loadUsers();
    loadArchiveStatus();
    const loadStats = async () => {
      try {
        setStatsLoading(true);
        const data = await getTenantStats(tenantId);
        setTenantStats(data.stats || []);
        setTenantStatsMonths(data.months || []);
      } catch (err) {
        console.error('Error loading tenant stats:', err);
      } finally {
        setStatsLoading(false);
      }
    };
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Poll archive status while an archive/restore job is in flight.
  const ARCHIVE_IN_PROGRESS = ['PENDING', 'ARCHIVING', 'VERIFYING', 'DELETING', 'RESTORING'];
  useEffect(() => {
    if (!ARCHIVE_IN_PROGRESS.includes(archiveInfo.archiveStatus)) return undefined;
    const timer = setInterval(async () => {
      const info = await loadArchiveStatus();
      if (info && !ARCHIVE_IN_PROGRESS.includes(info.archiveStatus)) {
        if (info.archiveStatus === 'ARCHIVED') {
          setTenant((prev) => (prev ? { ...prev, is_active: false } : prev));
          setSnackbar({ open: true, message: 'Tenant archived successfully', severity: 'success' });
          loadUsers();
        } else if (info.archiveStatus === 'NONE') {
          setSnackbar({ open: true, message: 'Tenant restored successfully', severity: 'success' });
          loadUsers();
        } else if (info.archiveStatus === 'FAILED') {
          const job = archiveInfo.archiveStatus === 'RESTORING' ? 'Restore' : 'Archive';
          setSnackbar({ open: true, message: `${job} job failed: ${info.archiveError || 'unknown error'}`, severity: 'error' });
          loadUsers();
        }
      }
    }, 4000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveInfo.archiveStatus, tenantId]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatDateStr = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const handleExtendTrial = async () => {
    try {
      setExtendingTrial(true);
      const endDateTimestamp = Math.floor(new Date(trialEndDate + 'T23:59:59').getTime() / 1000);
      await extendTrial(tenantId, {
        currentPeriodEnd: endDateTimestamp,
        scans: parseInt(trialScans, 10),
      });
      setSnackbar({ open: true, message: 'Trial extended successfully!', severity: 'success' });
      // Refresh billing data
      const billingData = await getTenantBilling(tenantId);
      const billing = Array.isArray(billingData) ? billingData : [];
      const planItem = billing.find(b => b.sk === 'plan') || null;
      setPlan(planItem);
      if (planItem?.data?.currentPeriodEnd) {
        setTrialEndDate(new Date(planItem.data.currentPeriodEnd * 1000).toISOString().split('T')[0]);
        setTrialScans(String(planItem.data.scans || 0));
      }
    } catch (err) {
      console.error('Error extending trial:', err);
      setSnackbar({ open: true, message: 'Failed to extend trial', severity: 'error' });
    } finally {
      setExtendingTrial(false);
    }
  };

  const handleAddUser = async () => {
    try {
      setAddUserSubmitting(true);
      await createTenantUser({
        userName: newUserName,
        userEmail: newUserEmail,
        userRole: newUserRole,
        tenantId: tenantId,
      });
      setSnackbar({ open: true, message: 'User created. Invitation email sent.', severity: 'success' });
      setAddUserDialog(false);
      setNewUserName('');
      setNewUserEmail('');
      setNewUserRole('TenantUser');
      await loadUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      const message = err?.response?.status === 409
        ? 'A user with this email already exists'
        : 'Failed to create user';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setAddUserSubmitting(false);
    }
  };

  const isTenantActive = tenant?.is_active !== false;

  const handleSoftPullToggle = async (event) => {
    const newValue = event.target.checked;
    setSoftPullUpdating(true);
    try {
      await updateTenantFeatures(tenantId, { softPullEnabled: newValue });
      setSoftPullEnabled(newValue);
      setSnackbar({ open: true, message: `Soft Pull ${newValue ? 'enabled' : 'disabled'} successfully`, severity: 'success' });
    } catch (err) {
      console.error('Error updating tenant features:', err);
      setSnackbar({ open: true, message: 'Failed to update feature flag', severity: 'error' });
    } finally {
      setSoftPullUpdating(false);
    }
  };

  const handleTenantStatusConfirm = async () => {
    setTenantStatusDialog(false);
    try {
      if (isTenantActive) {
        await deactivateTenant(tenantId);
        setTenant((prev) => ({ ...prev, is_active: false }));
        setSnackbar({ open: true, message: 'Tenant deactivated', severity: 'success' });
      } else {
        await activateTenant(tenantId);
        setTenant((prev) => ({ ...prev, is_active: true }));
        setSnackbar({ open: true, message: 'Tenant activated', severity: 'success' });
      }
      await loadUsers();
    } catch (err) {
      console.error('Error updating tenant status:', err);
      setSnackbar({ open: true, message: 'Failed to update tenant status', severity: 'error' });
    }
  };

  // A subscription is active unless it has been canceled or its billing period has lapsed.
  // Pay As You Go is not a subscription — there is nothing to cancel and its
  // currentPeriodEnd sits far in the future, so it would otherwise block archiving forever.
  // Mirrors is_active_subscription() in server/TenantManagementService/archive_safeguards.py.
  const nowSec = Math.floor(Date.now() / 1000);
  const subscriptionActive = !!plan?.data
    && !plan.data.isPAYG
    && !plan.data.canceled
    && Number(plan.data.currentPeriodEnd || 0) >= nowSec;
  const archiveInProgress = ARCHIVE_IN_PROGRESS.includes(archiveInfo.archiveStatus);
  // A FAILED status with the archive still in place means a restore did not finish;
  // the data is still archived, so offer Restore (retryable) rather than Archive.
  const restoreFailed = archiveInfo.archiveStatus === 'FAILED' && !!archiveInfo.archiveKey;
  const isArchived = archiveInfo.archiveStatus === 'ARCHIVED' || restoreFailed;
  const canArchive = !isTenantActive && !subscriptionActive && !archiveInProgress && !isArchived;
  const archiveBlockedReason = isTenantActive
    ? 'Tenant must be deactivated before archiving'
    : subscriptionActive
      ? 'Tenant has an active subscription; cancel it before archiving'
      : '';
  const archiveNameMatches =
    archiveConfirmText.trim() === (tenant?.business_name || tenant?.tenant_name || '').trim();

  const handleArchiveConfirm = async () => {
    setArchiveSubmitting(true);
    try {
      await archiveTenant(tenantId);
      setArchiveDialogOpen(false);
      setArchiveConfirmText('');
      setArchiveInfo((prev) => ({ ...prev, archiveStatus: 'PENDING', archiveError: null }));
      setSnackbar({ open: true, message: 'Archive started…', severity: 'info' });
    } catch (err) {
      console.error('Error archiving tenant:', err);
      const message = err?.response?.data?.message || 'Failed to start archive';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setArchiveSubmitting(false);
    }
  };

  const handleRestoreConfirm = async () => {
    setArchiveSubmitting(true);
    try {
      await restoreTenant(tenantId);
      setRestoreDialogOpen(false);
      setArchiveInfo((prev) => ({ ...prev, archiveStatus: 'RESTORING', archiveError: null }));
      setSnackbar({ open: true, message: 'Restore started…', severity: 'info' });
    } catch (err) {
      console.error('Error restoring tenant:', err);
      const message = err?.response?.data?.message || 'Failed to start restore';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setArchiveSubmitting(false);
    }
  };

  const handleUserActionClick = (e, user) => {
    e.stopPropagation();
    if (user.enabled) {
      setUserActionDialog({ open: true, user, action: 'disable' });
    } else if (isTenantActive) {
      setUserActionDialog({ open: true, user, action: 'enable' });
    }
  };

  const handleUserActionConfirm = async () => {
    const { user, action } = userActionDialog;
    setUserActionDialog({ open: false, user: null, action: null });
    try {
      if (action === 'disable') {
        await disableTenantUser(user.userName, tenantId);
        setSnackbar({ open: true, message: 'User disabled', severity: 'success' });
      } else {
        await enableTenantUser(user.userName, tenantId);
        setSnackbar({ open: true, message: 'User enabled', severity: 'success' });
      }
      await loadUsers();
    } catch (err) {
      console.error(`Error ${action === 'disable' ? 'disabling' : 'enabling'} user:`, err);
      setSnackbar({ open: true, message: `Failed to ${action} user`, severity: 'error' });
    }
  };

  const handleEditRoleConfirm = async () => {
    const { user, role } = editRoleDialog;
    setEditRoleDialog({ open: false, user: null, role: '' });
    try {
      await updateTenantUserRole(user.userName, tenantId, role);
      setSnackbar({ open: true, message: 'User role updated', severity: 'success' });
      await loadUsers();
    } catch (err) {
      console.error('Error updating user role:', err);
      setSnackbar({ open: true, message: 'Failed to update user role', severity: 'error' });
    }
  };

  const handleDeleteConfirm = async () => {
    const { user } = deleteDialog;
    setDeleteDialog({ open: false, user: null });
    try {
      await deleteTenantUser(user.userName, tenantId);
      setSnackbar({ open: true, message: 'User permanently deleted', severity: 'success' });
      await loadUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      const message = err?.response?.data?.message || 'Failed to delete user';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" py={10}>
          <CircularProgress />
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4">
            {tenant?.business_name || tenant?.tenant_name || tenantId}
          </SoftTypography>
          <SoftButton variant="gradient" color="secondary" onClick={() => navigate('/tenants')}>
            Back to Tenants
          </SoftButton>
        </SoftBox>

        {/* Tenant Details */}
        <Card>
          <SoftBox p={4}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <SoftTypography variant="h5" fontWeight="medium">
                Tenant Details
              </SoftTypography>
              <Chip
                label={isTenantActive ? 'Active' : 'Inactive'}
                color={isTenantActive ? 'success' : 'default'}
                onClick={() => setTenantStatusDialog(true)}
                clickable
              />
            </SoftBox>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography variant="caption" fontWeight="bold">Business Name *</SoftTypography>
                    <SoftInput
                      placeholder="Business Name"
                      name="businessName"
                      value={formik.values.businessName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.businessName && Boolean(formik.errors.businessName)}
                    />
                    {formik.touched.businessName && formik.errors.businessName && (
                      <SoftTypography variant="caption" color="error">{formik.errors.businessName}</SoftTypography>
                    )}
                  </SoftBox>
                  <SoftBox mb={2}>
                    <SoftTypography variant="caption" fontWeight="bold">Short Name *</SoftTypography>
                    <SoftInput
                      placeholder="tenant1"
                      name="tenantName"
                      value={formik.values.tenantName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.tenantName && Boolean(formik.errors.tenantName)}
                    />
                    {formik.touched.tenantName && formik.errors.tenantName && (
                      <SoftTypography variant="caption" color="error">{formik.errors.tenantName}</SoftTypography>
                    )}
                  </SoftBox>
                  <SoftBox mb={2}>
                    <SoftTypography variant="caption" fontWeight="bold">Email *</SoftTypography>
                    <SoftInput
                      placeholder="jsmith@example.com"
                      name="tenantEmail"
                      type="email"
                      value={formik.values.tenantEmail}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.tenantEmail && Boolean(formik.errors.tenantEmail)}
                    />
                    {formik.touched.tenantEmail && formik.errors.tenantEmail && (
                      <SoftTypography variant="caption" color="error">{formik.errors.tenantEmail}</SoftTypography>
                    )}
                  </SoftBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography variant="caption" fontWeight="bold">Phone *</SoftTypography>
                    <SoftInput
                      placeholder="1234567890"
                      name="tenantPhone"
                      value={formik.values.tenantPhone}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.tenantPhone && Boolean(formik.errors.tenantPhone)}
                    />
                    {formik.touched.tenantPhone && formik.errors.tenantPhone && (
                      <SoftTypography variant="caption" color="error">{formik.errors.tenantPhone}</SoftTypography>
                    )}
                  </SoftBox>
                  <SoftBox mb={2}>
                    <SoftTypography variant="caption" fontWeight="bold">Address</SoftTypography>
                    <SoftInput
                      placeholder="Address"
                      name="tenantAddress"
                      value={formik.values.tenantAddress}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </SoftBox>
                </Grid>
              </Grid>
              <SoftBox display="flex" gap={2} mt={1}>
                {!submitting ? (
                  <SoftButton type="submit" variant="gradient" color="info" disabled={!formik.isValid || !formik.dirty}>
                    Save Changes
                  </SoftButton>
                ) : (
                  <CircularProgress size={24} />
                )}
              </SoftBox>
            </form>
          </SoftBox>
        </Card>

        {/* Tenant Users */}
        <Card sx={{ mt: 3 }}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3} pb={0}>
            <SoftTypography variant="h5" fontWeight="medium">
              Tenant Users
            </SoftTypography>
            <SoftButton
              variant="gradient"
              color="info"
              size="small"
              onClick={() => setAddUserDialog(true)}
              disabled={!isTenantActive}
            >
              Add User
            </SoftButton>
          </SoftBox>
          <SoftBox p={3} pt={2}>
            {usersLoading ? (
              <SoftBox display="flex" justifyContent="center" alignItems="center" p={3}>
                <CircularProgress />
              </SoftBox>
            ) : users.length === 0 ? (
              <Alert severity="info">No users found for this tenant.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow>
                      <TableCell>Username</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Enabled</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user, index) => (
                      <TableRow key={user.userName || index}>
                        <TableCell>
                          <SoftTypography variant="caption" fontWeight="medium">
                            {user.userName}
                          </SoftTypography>
                        </TableCell>
                        <TableCell>
                          <SoftTypography variant="caption">
                            {user.email || '-'}
                          </SoftTypography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.userRole || '-'}
                            size="small"
                            color={ROLE_COLORS[user.userRole] || 'default'}
                            onClick={() => isTenantActive && user.enabled && setEditRoleDialog({ open: true, user, role: user.userRole || '' })}
                            clickable={isTenantActive && user.enabled}
                          />
                        </TableCell>
                        <TableCell>
                          <SoftTypography variant="caption">
                            {user.status || '-'}
                          </SoftTypography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.enabled ? 'Yes' : 'No'}
                            size="small"
                            color={user.enabled ? 'success' : 'default'}
                            onClick={(e) => isTenantActive && handleUserActionClick(e, user)}
                            clickable={isTenantActive}
                          />
                        </TableCell>
                        <TableCell>
                          <SoftTypography variant="caption">
                            {formatDateStr(user.created)}
                          </SoftTypography>
                        </TableCell>
                        <TableCell>
                          {!user.enabled && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => setDeleteDialog({ open: true, user })}
                              disabled={!isTenantActive}
                            >
                              Delete
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SoftBox>
        </Card>

        {/* Tenant Stats */}
        <Card sx={{ mt: 3 }}>
          <SoftBox p={3}>
            <SoftTypography variant="h5" fontWeight="medium" mb={2}>
              Usage Stats
            </SoftTypography>
            {statsLoading ? (
              <SoftBox display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </SoftBox>
            ) : !tenantStats || tenantStats.every((s) => s.lifetime === 0) ? (
              <Alert severity="info">No usage stats recorded for this tenant.</Alert>
            ) : (
              <>
                <Grid container spacing={2} mb={3}>
                  {tenantStats.filter((s) => s.lifetime > 0).map((s) => (
                    <Grid item xs={6} sm={4} md={3} key={s.type}>
                      <Card variant="outlined">
                        <SoftBox p={1.5} textAlign="center">
                          <SoftTypography variant="caption" color="text" fontWeight="medium">
                            {STAT_LABEL_MAP[s.type] || s.type}
                          </SoftTypography>
                          <SoftTypography variant="h5" fontWeight="bold">
                            {s.lifetime.toLocaleString()}
                          </SoftTypography>
                        </SoftBox>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                {tenantStatsMonths.length > 0 && (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={tenantStatsMonths.slice(-12).map((month) => {
                        const point = { month };
                        tenantStats.forEach((s) => {
                          point[s.type] = s.monthly[month] || 0;
                        });
                        return point;
                      })}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {tenantStats.filter((s) => s.lifetime > 0).map((s, i) => (
                        <Line
                          key={s.type}
                          type="monotone"
                          dataKey={s.type}
                          name={STAT_LABEL_MAP[s.type] || s.type}
                          stroke={STAT_COLORS[i % STAT_COLORS.length]}
                          strokeWidth={2}
                          dot={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </>
            )}
          </SoftBox>
        </Card>

        {/* Subscription Details */}
        <Card sx={{ mt: 3 }}>
          <SoftBox p={4}>
            <SoftTypography variant="h5" fontWeight="medium">
              Subscription Details
            </SoftTypography>

            {!plan ? (
              <SoftBox pt={2}>
                <SoftTypography variant="body2">
                  <strong>Plan: </strong> Free (No subscription)
                </SoftTypography>
              </SoftBox>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <SoftBox pt={2}>
                    <SoftTypography variant="body2">
                      <strong>Subscription Plan: </strong> {plan?.data?.descr || '-'}
                    </SoftTypography>
                  </SoftBox>

                  {plan?.data?.isTrial ? (
                    <>
                      <SoftBox pt={1}>
                        <SoftBadge
                          variant="gradient"
                          color="warning"
                          size="sm"
                          badgeContent="Trial"
                          container
                        />
                      </SoftBox>
                      {!plan?.data?.currentPeriodStart && !plan?.data?.currentPeriodEnd ? (
                        <SoftBox pt={2}>
                          <SoftBadge
                            variant="gradient"
                            color="info"
                            size="sm"
                            badgeContent="Pending TOS Acceptance"
                            container
                          />
                          <SoftBox pt={1}>
                            <SoftTypography variant="body2">
                              Trial will begin once the tenant accepts the Terms of Service.
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                      ) : (
                        <>
                          <SoftBox pt={2}>
                            <SoftTypography variant="body2">
                              <strong>Trial Start Date: </strong> {formatDate(plan?.data?.currentPeriodStart)}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox>
                            <SoftTypography variant="body2">
                              <strong>Trial End Date: </strong> {formatDate(plan?.data?.currentPeriodEnd)}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox pt={3}>
                            <SoftTypography variant="h6" fontWeight="medium" mb={1}>
                              Extend Trial
                            </SoftTypography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <SoftTypography variant="caption" fontWeight="bold">Trial End Date</SoftTypography>
                                <SoftInput
                                  type="date"
                                  value={trialEndDate}
                                  onChange={(e) => setTrialEndDate(e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <SoftTypography variant="caption" fontWeight="bold">Included ID Scans</SoftTypography>
                                <SoftInput
                                  type="number"
                                  value={trialScans}
                                  onChange={(e) => setTrialScans(e.target.value)}
                                />
                              </Grid>
                            </Grid>
                            <SoftBox mt={2}>
                              {!extendingTrial ? (
                                <SoftButton
                                  variant="gradient"
                                  color="warning"
                                  size="small"
                                  onClick={handleExtendTrial}
                                  disabled={!trialEndDate || !trialScans}
                                >
                                  Extend Trial
                                </SoftButton>
                              ) : (
                                <CircularProgress size={24} />
                              )}
                            </SoftBox>
                          </SoftBox>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <SoftBox>
                        <SoftTypography variant="body2">
                          <strong>Subscription Fee: </strong> ${plan?.data?.amount} ({plan?.data?.interval})
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox pt={2}>
                        <SoftTypography variant="body2">
                          <strong>Current Billing Cycle Start: </strong> {formatDate(plan?.data?.currentPeriodStart)}
                        </SoftTypography>
                      </SoftBox>
                      {plan?.data?.canceled ? (
                        <>
                          <SoftBox>
                            <SoftTypography variant="body2">
                              <strong>Plan Canceled on: </strong> {formatDate(plan?.data?.canceled)}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox>
                            <SoftTypography variant="body2">
                              <strong>Subscription Ends on: </strong> {formatDate(plan?.data?.currentPeriodEnd)}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox pt={1}>
                            <SoftBadge
                              variant="gradient"
                              color="error"
                              size="sm"
                              badgeContent="Canceled"
                              container
                            />
                          </SoftBox>
                        </>
                      ) : (
                        <SoftBox>
                          <SoftTypography variant="body2">
                            <strong>Next Bill Date: </strong> {formatDate(plan?.data?.currentPeriodEnd)}
                          </SoftTypography>
                        </SoftBox>
                      )}
                    </>
                  )}

                  {plan?.data?.subscription && (
                    <SoftBox pt={2}>
                      <SoftTypography variant="caption" color="text">
                        Stripe Subscription: {plan?.data?.subscription}
                      </SoftTypography>
                    </SoftBox>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  {plan?.data?.isTrial ? (
                    !plan?.data?.currentPeriodStart && !plan?.data?.currentPeriodEnd ? null : (
                    <>
                      <SoftBox pt={2}>
                        <SoftTypography variant="body2">
                          <strong>Included ID Scans: </strong> {plan?.data?.scans}
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox>
                        <SoftTypography variant="body2">
                          <strong>Scans Used: </strong> {plan?.data?.scansUsed}
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox>
                        <SoftTypography variant="body2">
                          <strong>Scans Remaining: </strong> {(plan?.data?.scans || 0) - (plan?.data?.scansUsed || 0)}
                        </SoftTypography>
                      </SoftBox>
                    </>
                    )
                  ) : (
                    <>
                      <SoftBox pt={2}>
                        <SoftTypography variant="body2">
                          <strong>Included ID Scans per Billing Cycle: </strong> {plan?.data?.scans} ({plan?.data?.interval})
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox>
                        <SoftTypography variant="body2">
                          <strong>Billing Cycle Scans Used: </strong> {plan?.data?.scansUsed}
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox>
                        <SoftTypography variant="body2">
                          <strong>Billing Cycle Scans Remaining: </strong> {(plan?.data?.scans || 0) - (plan?.data?.scansUsed || 0)}
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox>
                        <SoftTypography variant="body2">
                          <strong>Scan Overage Rate: </strong> ${plan?.data?.overage} per scan
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox pt={2}>
                        <SoftTypography variant="body2">
                          <strong>Billing Cycle Soft-Pulls Used: </strong> {plan?.data?.creditUsed}
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox>
                        <SoftTypography variant="body2">
                          <strong>Soft-Pull Rate: </strong> ${plan?.data?.credit} per pull
                        </SoftTypography>
                      </SoftBox>
                    </>
                  )}
                </Grid>
              </Grid>
            )}
          </SoftBox>
        </Card>
      </SoftBox>

      {/* Feature Flags */}
      <SoftBox mt={3}>
        <Card>
          <SoftBox p={3}>
            <SoftTypography variant="h6" fontWeight="medium" mb={2}>
              Feature Flags
            </SoftTypography>
            <FormControlLabel
              control={
                <Switch
                  checked={softPullEnabled}
                  onChange={handleSoftPullToggle}
                  disabled={softPullUpdating}
                  color="primary"
                />
              }
              label={
                <SoftBox>
                  <SoftTypography variant="body2" fontWeight="medium">
                    Soft Pull (Credit Check)
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text">
                    Allow this tenant to enable the Soft Pull form for credit tier checks
                  </SoftTypography>
                </SoftBox>
              }
            />
          </SoftBox>
        </Card>
      </SoftBox>

      {/* Danger Zone: Archive / Restore */}
      <SoftBox mb={3}>
        <Card sx={{ border: '1px solid', borderColor: 'error.main' }}>
          <SoftBox p={3}>
            <SoftTypography variant="h6" color="error" fontWeight="medium" mb={1}>
              Danger Zone
            </SoftTypography>

            {isArchived ? (
              <SoftBox>
                <SoftBox display="flex" alignItems="center" mb={1}>
                  <SoftBadge variant="contained" color="secondary" badgeContent="Archived" container />
                  <SoftTypography variant="button" color="text" ml={1}>
                    Archived {formatDate(archiveInfo.archivedAt)}
                    {archiveInfo.archiveCounts
                      ? ` — ${archiveInfo.archiveCounts.dynamoItems} records, ${archiveInfo.archiveCounts.docObjects} documents`
                      : ''}
                    {archiveInfo.archiveCounts?.cognitoUsers != null
                      ? `, ${archiveInfo.archiveCounts.cognitoUsers} users`
                      : ''}
                  </SoftTypography>
                </SoftBox>
                <SoftTypography variant="caption" color="text" mb={2} display="block">
                  This tenant&apos;s data has been exported and purged from production. Restoring
                  re-imports the data and documents; tenant users will receive new invitation emails
                  (their previous passwords cannot be restored).
                </SoftTypography>
                {restoreFailed && (
                  <SoftTypography variant="caption" color="error" mb={2} display="block">
                    Last restore did not complete: {archiveInfo.archiveError || 'unknown error'}.
                    Restoring again is safe — completed steps are skipped.
                  </SoftTypography>
                )}
                <SoftButton
                  color="success"
                  variant="gradient"
                  onClick={() => setRestoreDialogOpen(true)}
                  disabled={archiveInProgress}
                >
                  Restore Tenant
                </SoftButton>
              </SoftBox>
            ) : archiveInProgress ? (
              <SoftBox display="flex" alignItems="center">
                <CircularProgress size={20} color="error" />
                <SoftTypography variant="button" color="text" ml={1}>
                  {archiveInfo.archiveStatus === 'RESTORING' ? 'Restoring tenant…' : `Archiving tenant… (${archiveInfo.archiveStatus})`}
                </SoftTypography>
              </SoftBox>
            ) : (
              <SoftBox>
                <SoftTypography variant="caption" color="text" mb={2} display="block">
                  Archiving exports all of this tenant&apos;s data and scanned documents to a secure
                  archive, then permanently removes them from production. The tenant must be
                  deactivated and have no active subscription (Pay As You Go tenants are exempt).
                </SoftTypography>
                {archiveInfo.archiveStatus === 'FAILED' && (
                  <SoftTypography variant="caption" color="error" mb={2} display="block">
                    Previous archive attempt failed: {archiveInfo.archiveError || 'unknown error'}
                  </SoftTypography>
                )}
                <MuiTooltip title={canArchive ? '' : archiveBlockedReason}>
                  <span>
                    <SoftButton
                      color="error"
                      variant="gradient"
                      onClick={() => { setArchiveConfirmText(''); setArchiveDialogOpen(true); }}
                      disabled={!canArchive}
                    >
                      Archive Tenant
                    </SoftButton>
                  </span>
                </MuiTooltip>
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </SoftBox>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={() => { if (!archiveSubmitting) setArchiveDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Archive Tenant</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will export all data and scanned documents for{' '}
            <strong>{tenant?.business_name || tenant?.tenant_name}</strong> to a secure archive and
            then <strong>permanently delete</strong> them from production, including all Cognito
            users. This action cannot be undone (though the tenant can later be restored from its
            archive). Type the tenant name <strong>{tenant?.business_name || tenant?.tenant_name}</strong> to confirm.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            placeholder={tenant?.business_name || tenant?.tenant_name}
            value={archiveConfirmText}
            onChange={(e) => setArchiveConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)} disabled={archiveSubmitting}>Cancel</Button>
          {archiveSubmitting ? (
            <CircularProgress size={24} sx={{ mx: 2 }} />
          ) : (
            <Button
              onClick={handleArchiveConfirm}
              color="error"
              variant="contained"
              disabled={!archiveNameMatches}
            >
              Archive Permanently
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => { if (!archiveSubmitting) setRestoreDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Restore Tenant</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Restore <strong>{tenant?.business_name || tenant?.tenant_name}</strong> from its archive?
            This re-imports all archived data and documents. Tenant users will be recreated and
            receive new invitation emails (their previous passwords cannot be restored). The tenant
            will remain deactivated until you activate it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)} disabled={archiveSubmitting}>Cancel</Button>
          {archiveSubmitting ? (
            <CircularProgress size={24} sx={{ mx: 2 }} />
          ) : (
            <Button onClick={handleRestoreConfirm} color="success" variant="contained">
              Restore
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog
        open={addUserDialog}
        onClose={() => {
          if (!addUserSubmitting) {
            setAddUserDialog(false);
            setNewUserName('');
            setNewUserEmail('');
            setNewUserRole('TenantUser');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Tenant User</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a new user for this tenant. An invitation email will be sent.
          </DialogContentText>
          <TextField
            autoFocus
            label="Username"
            fullWidth
            variant="outlined"
            value={newUserName}
            onChange={(e) => setNewUserName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            label="Role"
            fullWidth
            variant="outlined"
            value={newUserRole}
            onChange={(e) => setNewUserRole(e.target.value)}
          >
            <MenuItem value="TenantAdmin">TenantAdmin (Hidden from tenant dashboard)</MenuItem>
            <MenuItem value="Admin">Admin (Full tenant admin)</MenuItem>
            <MenuItem value="Dashboard">Dashboard (Tenant dashboard only )</MenuItem>
            {/*<MenuItem value="TenantUser">TenantUser</MenuItem>*/}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddUserDialog(false);
              setNewUserName('');
              setNewUserEmail('');
              setNewUserRole('TenantUser');
            }}
            disabled={addUserSubmitting}
          >
            Cancel
          </Button>
          {addUserSubmitting ? (
            <CircularProgress size={24} sx={{ mx: 2 }} />
          ) : (
            <Button
              onClick={handleAddUser}
              variant="contained"
              disabled={!newUserName || !newUserEmail}
            >
              Create User
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Enable/Disable User Confirmation Dialog */}
      <Dialog
        open={userActionDialog.open}
        onClose={() => setUserActionDialog({ open: false, user: null, action: null })}
      >
        <DialogTitle>{userActionDialog.action === 'enable' ? 'Enable' : 'Disable'} User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {userActionDialog.action}{' '}
            <strong>{userActionDialog.user?.email || userActionDialog.user?.userName}</strong>?
            {userActionDialog.action === 'disable'
              ? ' This will prevent them from logging in.'
              : ' This will allow them to log in again.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserActionDialog({ open: false, user: null, action: null })}>Cancel</Button>
          <Button
            onClick={handleUserActionConfirm}
            color={userActionDialog.action === 'enable' ? 'success' : 'error'}
            variant="contained"
          >
            {userActionDialog.action === 'enable' ? 'Enable' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tenant Status Toggle Dialog */}
      <Dialog
        open={tenantStatusDialog}
        onClose={() => setTenantStatusDialog(false)}
      >
        <DialogTitle>{isTenantActive ? 'Deactivate' : 'Activate'} Tenant</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {isTenantActive ? 'deactivate' : 'activate'}{' '}
            <strong>{tenant?.business_name || tenant?.tenant_name}</strong>?
            {isTenantActive && ' All tenant users will be disabled and the tenant will lose access.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTenantStatusDialog(false)}>Cancel</Button>
          <Button
            onClick={handleTenantStatusConfirm}
            color={isTenantActive ? 'error' : 'success'}
            variant="contained"
          >
            {isTenantActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog
        open={editRoleDialog.open}
        onClose={() => setEditRoleDialog({ open: false, user: null, role: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit User Role</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Change role for <strong>{editRoleDialog.user?.email || editRoleDialog.user?.userName}</strong>
          </DialogContentText>
          <TextField
            select
            label="Role"
            fullWidth
            variant="outlined"
            value={editRoleDialog.role}
            onChange={(e) => setEditRoleDialog((prev) => ({ ...prev, role: e.target.value }))}
          >
            <MenuItem value="TenantAdmin">TenantAdmin (Hidden from tenant dashboard)</MenuItem>
            <MenuItem value="Admin">Admin (Full tenant admin)</MenuItem>
            <MenuItem value="Dashboard">Dashboard (Tenant dashboard only)</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRoleDialog({ open: false, user: null, role: '' })}>Cancel</Button>
          <Button
            onClick={handleEditRoleConfirm}
            variant="contained"
            disabled={!editRoleDialog.role || editRoleDialog.role === editRoleDialog.user?.userRole}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, user: null })}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete{' '}
            <strong>{deleteDialog.user?.email || deleteDialog.user?.userName}</strong>?
            This action cannot be undone. The user will be removed from the Cognito user pool.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default TenantDashboard;
